import { useCallback, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { UseFormReturn } from 'react-hook-form';
import { checkSlugUniqueness } from '../api/sitesApi';
import { SiteImportFormData } from '../schemas/siteImportSchema';
import { VALIDATION_DEBOUNCE_MS } from './validationConstants';

export function useCheckSlugUniqueness(
  form: UseFormReturn<SiteImportFormData>
) {
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCheckedSlugRef = useRef<string>('');

  const mutation = useMutation({
    mutationFn: (slug: string) => checkSlugUniqueness(slug),
    onSuccess: (result, slug) => {
      if (slug.toUpperCase() === lastCheckedSlugRef.current.toUpperCase()) {
        if (result.isUnique) {
          form.clearErrors('slug');
        } else {
          form.setError('slug', {
            type: 'manual',
            message: result.errorMessage ?? 'This slug is already in use',
          });
        }
      }
    },
  });

  const mutateRef = useRef(mutation.mutate);
  mutateRef.current = mutation.mutate;

  const checkSlug = useCallback((slug: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const upperSlug = slug.toUpperCase();
    lastCheckedSlugRef.current = upperSlug;

    if (!/^[A-Z0-9]{4}$/.test(upperSlug)) {
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      mutateRef.current(upperSlug);
    }, VALIDATION_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return { checkSlug, isChecking: mutation.isPending };
}
